import React, { useState, forwardRef, FocusEvent, ChangeEvent } from 'react';
import { ErrorText } from './ErrorText';
import { DateInput } from './DateInput';
import * as S from './Input.styles';
import { IconPositionInInput, IInputProps } from './Input.model';
import CharacterCounter from '../CharacterCounter/CharacterCounter';
import { useCharacterCounter } from '../../../hooks/useCharacterCounter/useCharacterCounter';

export const Input = forwardRef<HTMLInputElement, IInputProps>(
  (
    {
      iconPosition,
      error,
      icon,
      disabled,
      readonly,
      type,
      defaultValue,
      onClick,
      onChangeDateInput,
      placeholder,
      onDateClickOutSide,
      border,
      requiredText,
      onBlur,
      onChange,
      subLabel,
      isDatePickerOpen,
      ignoreDateErrors,
      dateInputStart,
      customStyles,
      maxLength,
      hasCounter,
      value,
      ...restProps
    },
    ref,
  ) => {
    const [innerError, setInnerError] = useState<string>();

    const {
      currentLength,
      maxLength: counterMaxLength,
      isError: counterIsError,
    } = useCharacterCounter(value || '', maxLength);

    const err = error ?? innerError;

    const newValue = (e: FocusEvent<HTMLInputElement>): void => {
      e.target.value = e.target.value.trim();

      onChange && onChange(e);
    };

    return (
      <S.InputWrapper disabled={disabled} border={border}>
        {type === 'date' ? (
          <DateInput
            type={type}
            icon={icon}
            iconPosition={iconPosition}
            onClick={onClick}
            disabled={disabled}
            onChangeDateInput={onChangeDateInput}
            defaultValue={defaultValue}
            onDateClickOutSide={onDateClickOutSide}
            placeholder={placeholder}
            error={err}
            setError={ignoreDateErrors ? undefined : setInnerError}
            requiredText={requiredText}
            isDatePickerOpen={isDatePickerOpen}
            dateInputStart={dateInputStart}
            customStyles={customStyles}
          />
        ) : (
          <S.StyledInput
            ref={ref}
            maxLength={maxLength}
            icon={icon}
            iconPosition={iconPosition}
            disabled={disabled}
            readOnly={readonly}
            type={type}
            defaultValue={defaultValue}
            placeholder={placeholder}
            error={err}
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              onBlur && onBlur(e);
              newValue(e);
            }}
            onChange={onChange}
            customStyles={customStyles}
            value={value}
            {...restProps}
          />
        )}

        {hasCounter && (
          <CharacterCounter
            currentLength={currentLength}
            maxLength={counterMaxLength}
            isError={counterIsError}
          />
        )}

        {err && <ErrorText error={err} />}
        {subLabel && !err && (
          <S.SubLabelContainer>{subLabel}</S.SubLabelContainer>
        )}

        {icon && (
          <S.IconBox iconPosition={iconPosition ?? IconPositionInInput.left}>
            {icon}
          </S.IconBox>
        )}
      </S.InputWrapper>
    );
  },
);
